"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

/* =============================================================================
   The Picks playing card, face down.

   The one Alexei kept (2026-09-16): the wheel's share card plate at poker
   proportions, a double hairline frame inset from the edge, and the mark
   solid in the centre. One mark. No glow, no sheen, no second mark in the
   ground: he called those out. Depth comes from shade, not shine: the plate
   darkens toward its edges, the frame is pressed into it (a dark line under
   a light one), the mark is printed in relief (a dark copy under it and a
   brushed fill), and the card has a thickness at its lower edge.
   ============================================================================= */

/* Copied verbatim from src/components/arena/v2/primitives.tsx (public/picks-mark.svg). */
export const PICKS_MARK_PATH =
  "M24.3435 26.2618C24.559 26.3092 24.6638 26.5545 24.5492 26.7429L22.0537 30.843C21.9764 30.97 21.8221 31.0274 21.6805 30.9818L18.0184 29.8024C17.9448 29.7787 17.8822 29.7292 17.8421 29.6631L14.8521 24.7335C14.7055 24.4918 14.9198 24.1921 15.1959 24.2528L24.3435 26.2618ZM27.5723 21.7849C27.5039 21.8971 27.374 21.9564 27.2444 21.9345L11.6594 19.3037C11.5675 19.2882 11.4868 19.2335 11.4383 19.1539L8.71924 14.6873C8.58521 14.4671 8.75181 14.1866 9.00927 14.199L22.4215 14.8443C22.5176 14.8489 22.6107 14.8103 22.6752 14.739L29.1237 7.62029C29.3108 7.41366 29.1642 7.08301 28.8854 7.08301H10.0811C9.98691 7.08301 9.89743 7.12434 9.83635 7.19609L6.80359 10.7584C6.66169 10.9251 6.39832 10.9043 6.28435 10.7174L0.047105 0.48883C-0.0835217 0.274612 0.0706639 0 0.321567 0H40.2915C40.5425 0 40.6967 0.274685 40.566 0.4889L27.5723 21.7849Z"

const GROUND =
  "linear-gradient(118deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)"
const LAMP =
  "radial-gradient(130% 180% at 12% 4%, hsl(var(--primary-0) / 0.34) 0%, transparent 52%), linear-gradient(298deg, hsl(var(--black-500)) 0%, transparent 42%)"
const BLOOM =
  "radial-gradient(120% 150% at 94% 100%, hsl(var(--primary-0) / 0.14) 0%, transparent 52%)"

export interface CardBackProps {
  /** Card width in px; height follows poker proportions. */
  width?: number
  className?: string
}

export function CardBack({ width = 300, className }: CardBackProps) {
  const id = React.useId()
  const fillId = `${id}-fill`
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-[18px] ring-1 ring-inset ring-white-100",
        className
      )}
      style={{
        width,
        height: width * 1.4,
        background: GROUND,
        // Thickness: the lower edge falls away into shadow, the top edge catches a hair of light.
        boxShadow:
          "inset 0 -2px 0 hsl(var(--black-0) / 0.6), inset 0 1px 0 hsl(var(--white-0) / 0.06)",
      }}
    >
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: LAMP }} />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.16,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to right, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to right, black 0%, transparent 80%)",
        }}
      />
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: BLOOM }} />
      {/* Shade: the plate darkens toward its edges, so the middle stands proud. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 50% 48%, transparent 45%, hsl(var(--black-0) / 0.28) 80%, hsl(var(--black-0) / 0.5) 100%)",
        }}
      />

      {/* The frame, pressed into the plate: a dark line one pixel below a
          light one reads as a groove, not a drawn border. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px]"
        style={{ boxShadow: "inset 0 1px 0 hsl(var(--white-0) / 0.14), inset 0 -1px 0 hsl(var(--white-0) / 0.05), 0 1px 0 hsl(var(--black-0) / 0.6)" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px] ring-1 ring-inset ring-white-200"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[16px] top-[16px] bottom-[34px] rounded-[7px] ring-1 ring-inset ring-white-100"
      />

      {/* The foot band, same as the face. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-[10px] text-center font-open-sauce text-[9px] font-semibold tracking-tight text-white/35">
        go.getpicks.app
      </span>

      {/* The mark, printed in relief: a dark copy a touch below it is the
          shadow the raised print throws, the fill is brushed, lighter at the
          top, and a hairline of the ground shows along its lower edges. */}
      <svg
        viewBox="0 0 42 34"
        aria-hidden
        className="absolute left-1/2 top-[calc(50%-9px)] w-[40%] -translate-x-1/2 -translate-y-1/2"
      >
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0.2" y2="1">
            <stop offset="0" stopColor="hsl(var(--white-0))" />
            <stop offset="0.55" stopColor="hsl(0 0% 88%)" />
            <stop offset="1" stopColor="hsl(0 0% 74%)" />
          </linearGradient>
        </defs>
        <g transform="translate(0.65 1.5)">
          <path d={PICKS_MARK_PATH} fill="hsl(var(--black-0))" fillOpacity={0.7} transform="translate(0 1.4)" />
          <path d={PICKS_MARK_PATH} fill={`url(#${fillId})`} />
          <path d={PICKS_MARK_PATH} fill="none" stroke="hsl(var(--white-0))" strokeOpacity={0.35} strokeWidth={0.35} />
        </g>
      </svg>
    </div>
  )
}
